import React, { useEffect, useRef, useState } from 'react';
import Popup from '../../lib/popup/Popup';
import TextField from '../../lib/text-field/TextField';
import './notifications.css';
import notifications, {
	NotificationSeverity,
	Notification,
} from '../../services/notifications';
import { readableTimeFromNow } from '../../utils/date';
import { IComponentLifecycleProps } from '../../utils/react';
import { useParams } from 'react-router-dom';
import { EditorService } from '../../services/editor';
import dayjs from 'dayjs';

export interface INotificationsPanelProps extends IComponentLifecycleProps {
	project: EditorService;
	isOpen: boolean;
}

export function resolveSeverityIcon(severity: NotificationSeverity) {
	switch (severity) {
		case NotificationSeverity.Error:
			return 'fa-circle-exclamation';
		case NotificationSeverity.Warning:
			return 'fa-triangle-exclamation';
		case NotificationSeverity.Info:
			return 'fa-circle-info';
		case NotificationSeverity.Success:
			return 'fa-circle-check';
		default:
			return 'fa-circle-info';
	}
}

export default function NotificationsPanel(props: INotificationsPanelProps) {
	const [searchValue, setSearchValue] = useState<string>('');
	const [filters, setFilters] = useState<NotificationSeverity[]>([]);
	const [showPreviouslySeen, setShowPreviouslySeen] = useState<boolean>(false);
	const [results, setResults] = useState<Notification[]>([]);
	const inputElementRef = useRef(null);
	const { id: projectId } = useParams<{ id: string }>();

	const toggleSeenFilter = () => {
		setShowPreviouslySeen(!showPreviouslySeen);
	};

	const getResults = async (searchQuery: string = '') => {
		const searchableNotifications: Notification[] = [];

		const existingFilters = [...filters];

		// Include entity types on search array only if they are selected, or if no filters are selected
		if (
			existingFilters.includes(NotificationSeverity.Error) ||
			!existingFilters.length
		) {
			searchableNotifications.push(
				...(showPreviouslySeen
					? notifications.getChannel('project--' + projectId).getErrors()
					: notifications.getChannel('project--' + projectId).getUnseenErrors())
			);
		}

		if (
			existingFilters.includes(NotificationSeverity.Warning) ||
			!existingFilters.length
		) {
			searchableNotifications.push(
				...(showPreviouslySeen
					? notifications.getChannel('project--' + projectId).getWarnings()
					: notifications
							.getChannel('project--' + projectId)
							.getUnseenWarnings())
			);
		}

		if (
			existingFilters.includes(NotificationSeverity.Success) ||
			!existingFilters.length
		) {
			searchableNotifications.push(
				...(showPreviouslySeen
					? notifications.getChannel('project--' + projectId).getSuccess()
					: notifications
							.getChannel('project--' + projectId)
							.getUnseenSuccess())
			);
		}

		if (
			existingFilters.includes(NotificationSeverity.Info) ||
			!existingFilters.length
		) {
			searchableNotifications.push(
				...(showPreviouslySeen
					? notifications.getChannel('project--' + projectId).getInfos()
					: notifications.getChannel('project--' + projectId).getUnseenInfos())
			);
		}

		// Sort alphabetically
		const timeSorted = searchableNotifications.sort((a, b) => {
			const aName = a.createdAt;
			const bName = b.createdAt;

			if (aName < bName) {
				return -1;
			}

			if (aName > bName) {
				return 1;
			}

			return 0;
		});

		if (!searchQuery) {
			return timeSorted;
		}

		const filteredNotifications = timeSorted.filter((notification) => {
			return notification.title
				.toLowerCase()
				.includes(searchQuery.toLowerCase());
		});

		// Sort by title matching query
		const sortedNotifications = filteredNotifications.sort((a, b) => {
			const aName = a.title.toLowerCase();
			const bName = b.title.toLowerCase();

			if (aName.includes(searchQuery.toLowerCase())) {
				return -1;
			}

			if (bName.includes(searchQuery.toLowerCase())) {
				return 1;
			}

			return 0;
		});

		return sortedNotifications;
	};

	const toggleEntityFilter = async (
		NotificationSeverity: NotificationSeverity
	) => {
		const existingFilters = [...filters];

		// If the entity is not already in the array, we add it
		if (!existingFilters.includes(NotificationSeverity)) {
			setFilters([...existingFilters, NotificationSeverity]);

			// If the entity is already in the array, we remove it
		} else {
			setFilters(
				existingFilters.filter(
					(type: NotificationSeverity) => type !== NotificationSeverity
				)
			);
		}
	};

	const loadResults = async () => {
		const sortedNotifications = await getResults(searchValue);
		setResults(sortedNotifications);
	};

	const onResultClick = (notification: Notification) => {
		notification.executeAction();
	};

	useEffect(() => {
		const update = () => {
			if (!props.project?.logic) {
				return;
			}
			loadResults();
		};

		notifications.getChannel('project--' + projectId).on('new', update);

		notifications.getChannel('project--' + projectId).on('remove', update);

		loadResults();

		return () => {
			notifications.getChannel('project--' + projectId).off('new', update);
			notifications.getChannel('project--' + projectId).off('remove', update);
		};
	}, [props.project?.logic]);

	useEffect(() => {
		if (props.isOpen && props.project?.canvas) {
			props.project.canvas.ignoreNextClick = true;
			const input = inputElementRef.current;

			if (input && document.activeElement !== input) {
				// Adding a slight delay to ensure the element is available
				setTimeout(() => {
					input.focus();
				}, 0);
			}
		}
	}, [props.isOpen]);

	useEffect(() => {
		loadResults();
	}, [searchValue, filters, showPreviouslySeen]);

	const { isOpen } = props;

	return (
		<Popup
			anchorQuerySelector='#notifications-toolbar-button'
			placement='left-start'
			open={isOpen}
			arrowClasses={['dropdown-menu-card-arrow']}
			containerClasses={['popup__notifications']}
			onClick={(e: any) => {
				e.stopPropagation();
			}}
		>
			<div className='notifications-panel__container'>
				<TextField
					ref={inputElementRef}
					variant='filled'
					label='Search notifications'
					iconEnd='fa-solid fa-magnifying-glass'
					value={searchValue}
					onChange={(value: any) => {
						setSearchValue(value);
					}}
					// onDebouncedInput={(value: any) => {
					// 	onSearch(value);
					// }}
				/>

				{/* Entity types' filters */}
				<span className='notifications-panel__filters'>
					<p className='notifications-panel__filters__label'>Filters</p>
					<div className='notifications-panel__filters__buttons'>
						<button
							style={
								filters.includes(NotificationSeverity.Error) || !filters.length
									? { color: 'var(--color-main-error)' }
									: undefined
							}
							className='toolbar__button'
							onClick={() => {
								toggleEntityFilter(NotificationSeverity.Error);
							}}
						>
							<i className='fa-solid fa-circle-exclamation' />
							<p>Errors</p>
						</button>

						<button
							style={
								filters.includes(NotificationSeverity.Warning) ||
								!filters.length
									? { color: 'var(--color-main-warning)' }
									: undefined
							}
							className='toolbar__button'
							onClick={() => {
								toggleEntityFilter(NotificationSeverity.Warning);
							}}
						>
							<i className='fa-solid fa-triangle-exclamation' />
							<p>Warnings</p>
						</button>

						<button
							style={
								filters.includes(NotificationSeverity.Info) || !filters.length
									? { color: 'var(--color-main-primary)' }
									: undefined
							}
							className='toolbar__button'
							onClick={() => {
								toggleEntityFilter(NotificationSeverity.Info);
							}}
						>
							<i className='fa-solid fa-circle-info' />
							<p>Info</p>
						</button>

						<button
							style={
								filters.includes(NotificationSeverity.Success) ||
								!filters.length
									? { color: 'var(--color-main-success)' }
									: undefined
							}
							className='toolbar__button'
							onClick={() => {
								toggleEntityFilter(NotificationSeverity.Success);
							}}
						>
							<i className='fa-solid fa-circle-check' />
							<p>Done</p>
						</button>

						<button
							style={showPreviouslySeen ? { color: 'black' } : undefined}
							className='toolbar__button'
							onClick={() => {
								toggleSeenFilter();
							}}
						>
							<i className='fa-solid fa-box-archive' />
							<p>Seen</p>
						</button>
					</div>
				</span>

				<ul
					className='notifications-panel__results'
					onScroll={(e: any) => {
						e.stopPropagation();
						document.body.style.overflow = 'hidden';
					}}
				>
					{results.length ? (
						results
							.sort((a, b) => {
								const aDate = a.createdAt;
								const bDate = b.createdAt;

								if (dayjs(aDate).isAfter(dayjs(bDate))) {
									return -1;
								}

								if (dayjs(aDate).isBefore(dayjs(bDate))) {
									return 1;
								}

								return 0;
							})
							.map((notification: Notification) => (
								<li
									key={notification.uuid}
									className='notifications-panel__results__item'
								>
									<button
										className='notifications-panel__results__item-button'
										onClick={() => {
											onResultClick(notification);
										}}
									>
										<i
											className={`fa-solid ${resolveSeverityIcon(
												notification.severity
											)}`}
										/>
										<span className='notifications-panel__results__item__body'>
											<span className='notifications-panel__results__item__header-line'>
												<p className='notifications-panel__results__item__title'>
													{notification.title}
												</p>
												<p className='notifications-panel__results__item__time'>
													{readableTimeFromNow(
														new Date(notification.createdAt)
													)}
												</p>
											</span>
											<p className='notifications-panel__results__item__description'>
												{notification.message}
											</p>
										</span>
									</button>
								</li>
							))
					) : (
						<p className='notifications-panel__results__item__empty'>
							No new notifications
						</p>
					)}
				</ul>
			</div>
		</Popup>
	);
}
